import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface TransferRulesSettingsProps {
  companyId: string;
}

interface RuleTemplate {
  type: string;
  label: string;
  description: string;
  defaultParams: Record<string, any>;
  paramFields?: Array<{
    key: string;
    label: string;
    type: string;
    suffix?: string;
  }>;
}

const ruleTemplates: RuleTemplate[] = [
  {
    type: 'BOARD_APPROVAL',
    label: 'Board Approval Required',
    description: 'Require board resolution for transfers above threshold',
    defaultParams: { required_for_threshold_pct: 5, approval_within_days: 30 },
    paramFields: [
      { key: 'required_for_threshold_pct', label: 'Threshold', type: 'number', suffix: '%' },
      { key: 'approval_within_days', label: 'Approval Period', type: 'number', suffix: 'days' },
    ],
  },
  {
    type: 'ROFR',
    label: 'Right of First Refusal',
    description: 'Existing shareholders have first right to purchase',
    defaultParams: { offer_period_days: 30, match_required: true },
    paramFields: [
      { key: 'offer_period_days', label: 'ROFR Period', type: 'number', suffix: 'days' },
    ],
  },
];

export function TransferRulesSettings({ companyId }: TransferRulesSettingsProps) {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRules();
  }, [companyId]);

  async function loadRules() {
    try {
      const { data, error } = await supabase
        .from('company_transfer_rules')
        .select('*')
        .eq('company_id', companyId);

      if (error) throw error;
      setRules(data || []);
    } catch (error: any) {
      console.error('Error loading transfer rules:', error);
      toast.error('Failed to load transfer rules');
    } finally {
      setLoading(false);
    }
  }

  async function toggleRule(ruleType: string, enabled: boolean) {
    setSaving(true);
    try {
      const existingRule = rules.find((r) => r.rule_type === ruleType);
      const template = ruleTemplates.find((t) => t.type === ruleType);

      if (existingRule) {
        const { error } = await supabase
          .from('company_transfer_rules')
          .update({ enabled })
          .eq('id', existingRule.id);

        if (error) throw error;
      } else if (enabled && template) {
        const { error } = await supabase.from('company_transfer_rules').insert({
          company_id: companyId,
          rule_type: ruleType,
          enabled: true,
          parameters: template.defaultParams,
        });

        if (error) throw error;
      }

      await loadRules();
      toast.success(enabled ? 'Rule enabled' : 'Rule disabled');
    } catch (error: any) {
      console.error('Error toggling rule:', error);
      toast.error('Failed to update rule');
    } finally {
      setSaving(false);
    }
  }

  async function updateRuleParams(ruleType: string, params: Record<string, any>) {
    setSaving(true);
    try {
      const existingRule = rules.find((r) => r.rule_type === ruleType);
      if (!existingRule) return;

      const { error } = await supabase
        .from('company_transfer_rules')
        .update({ parameters: params })
        .eq('id', existingRule.id);

      if (error) throw error;

      await loadRules();
      toast.success('Rule parameters updated');
    } catch (error: any) {
      console.error('Error updating rule params:', error);
      toast.error('Failed to update parameters');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transfer Restrictions</CardTitle>
        <CardDescription>
          Configure rules that govern share transfers in this company
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {ruleTemplates.map((template) => {
          const existingRule = rules.find((r) => r.rule_type === template.type);
          const isEnabled = existingRule?.enabled || false;

          return (
            <div key={template.type} className="space-y-3 border-b pb-4 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label className="text-base">{template.label}</Label>
                  <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(checked) => toggleRule(template.type, checked)}
                  disabled={saving}
                />
              </div>

              {isEnabled && template.paramFields && (
                <div className="ml-4 space-y-2 pt-2">
                  {template.paramFields.map((field) => (
                    <div key={field.key} className="flex items-center gap-2">
                      <Label className="text-sm min-w-[140px]">{field.label}:</Label>
                      <Input
                        type={field.type}
                        value={existingRule?.parameters?.[field.key] || ''}
                        onChange={(e) => {
                          const newParams = {
                            ...existingRule.parameters,
                            [field.key]:
                              field.type === 'number' ? Number(e.target.value) : e.target.value,
                          };
                          updateRuleParams(template.type, newParams);
                        }}
                        className="w-24"
                      />
                      {field.suffix && (
                        <span className="text-sm text-muted-foreground">{field.suffix}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
